import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import api from './services/api';

// Pages
import LandingPage from './pages/LandingPage';
import AssessmentPage from './pages/AssessmentPage';
import RegistrationPage from './pages/RegistrationPage';
import ResultsPage from './pages/ResultsPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" state={{ from: location }} replace />;
};

// Lightweight backend keep-alive
const BackendKeepAlive = () => {
  useEffect(() => {
    let isMounted = true;

    const pingBackend = async () => {
      if (!isMounted) return;

      try {
        await api.get('/health');
      } catch (error) {
        // Intentionally silent
      }
    };

    pingBackend();
    const intervalId = window.setInterval(pingBackend, 4 * 60 * 1000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
};

// Start Page Component (shows important note)
const StartPage = () => {
  const [accepted, setAccepted] = React.useState(false);
  const { language, t } = useLanguage();
  const { user } = useAuth();

  if (!accepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-brand-50/20 to-white flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-xl p-8 border border-gray-200">
          <button
            onClick={() => window.history.back()}
            className="mb-4 text-sm font-medium text-gray-600 hover:text-brand-600 flex items-center gap-1 transition-colors"
            data-testid="back-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            {language === 'fr' ? 'Retour' : 'Back'}
          </button>
          <h1 className="text-3xl font-black text-gray-900 mb-4">
            {t('beforeYouBegin')}
          </h1>
          <div className="bg-brand-50 border-l-4 border-brand-600 p-4 mb-6">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong className="font-bold">{t('importantNote')}</strong> {t('importantNoteText')}
            </p>
          </div>
          <p className="text-gray-700 mb-6">
            {t('assessmentDesc')}
          </p>
          <button
            onClick={() => setAccepted(true)}
            className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-4 px-8 rounded-lg transition-colors"
            data-testid="accept-and-begin-btn"
          >
            {t('iUnderstand')}
          </button>
        </div>
      </div>
    );
  }

  return <Navigate to="/assessment/questions" />;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <BackendKeepAlive />

          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/assessment/start" element={<StartPage />} />
              <Route path="/assessment/questions" element={<AssessmentPage />} />
              <Route path="/assessment/register" element={<RegistrationPage />} />
              <Route path="/assessment/results" element={<ResultsPage />} />
              <Route path="/login" element={<LoginPage />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>

            <Toaster position="top-center" richColors expand={true} />
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
